import {
  Pagination as BasePagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

interface PaginationComponentProps {
  currentPage: number;
  totalPages: number;
  className?: string;
  onPageChange: (page: number) => void;
}

export const Pagination = ({
  currentPage,
  totalPages,
  className,
  onPageChange,
}: PaginationComponentProps) => {
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const range = 2; // Show 2 pages before and after the current page

    // Always show first page
    pages.push(1);

    // Add ellipsis if needed before the middle range
    if (currentPage - range > 2) {
      pages.push('...');
    }

    // Add middle range pages
    for (
      let i = Math.max(2, currentPage - range);
      i <= Math.min(totalPages - 1, currentPage + range);
      i++
    ) {
      pages.push(i);
    }

    // Add ellipsis if needed after the middle range
    if (currentPage + range < totalPages - 1) {
      pages.push('...');
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <BasePagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            className={
              currentPage === 1
                ? 'pointer-events-none opacity-50'
                : 'cursor-pointer'
            }
            onClick={(event) => {
              event.preventDefault();
              onPageChange(currentPage - 1);
            }}
          />
        </PaginationItem>

        {getPageNumbers().map((page, i) =>
          typeof page === 'number' ? (
            <PaginationItem
              key={`PaginationItem-${i}`}
              className='cursor-pointer'
            >
              <PaginationLink
                isActive={currentPage === page}
                onClick={(event) => {
                  event.preventDefault();
                  onPageChange(page);
                }}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          ) : (
            <PaginationItem key={`PaginationEllipsis-${i}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ),
        )}
        <PaginationItem>
          <PaginationNext
            className={
              currentPage === totalPages
                ? 'pointer-events-none opacity-50'
                : 'cursor-pointer'
            }
            onClick={(event) => {
              event.preventDefault();
              onPageChange(currentPage + 1);
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </BasePagination>
  );
};
